import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import type { ReactNode } from 'react';

type Props = {
  count: number;
  /** Right-side action buttons. */
  actions: ReactNode;
  onClear: () => void;
};

/**
 * Sticky bar that appears above the table when one or more rows are
 * checked. Hosts bulk action buttons + a "clear selection" handle.
 *
 *   <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}
 *                  actions={<Button onClick={...}>Assign Structure</Button>} />
 *
 * Styling note: the bar restyles whatever action <Button>s it's handed into a
 * uniform white-on-accent look, so callers don't each have to theme them for
 * the coloured surface.
 */
export function BulkActionBar({ count, actions, onClear }: Props) {
  if (count === 0) return null;
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'sticky',
        top: 8,
        zIndex: 5,
        mb: 2,
        px: 1.75,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        borderRadius: 2,
        // Soft accent surface with an accent stripe — reads as a highlight, not
        // a heavy solid-blue banner.
        bgcolor: '#eef6fd',
        border: '1px solid #cfe6fb',
        borderLeft: '4px solid #108ae6',
        boxShadow: '0 6px 18px rgba(16,138,230,0.12)',
        animation: 'bulkbar-in 200ms ease-out',
        '@keyframes bulkbar-in': {
          from: { opacity: 0, transform: 'translateY(-6px)' },
          to: { opacity: 1, transform: 'none' },
        },
        // Uniform action buttons: white chips with accent text, regardless of
        // the variant/color the caller passed.
        '& .MuiButton-root': {
          backgroundColor: '#fff',
          color: '#0d79c7',
          border: '1px solid #cfe6fb',
          boxShadow: 'none',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#fff',
            borderColor: '#108ae6',
            boxShadow: '0 2px 8px rgba(16,138,230,0.18)',
          },
          '&.Mui-disabled': {
            backgroundColor: '#f3f7fb',
            color: '#9aa4b0',
            borderColor: '#e2e8f0',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            minWidth: 26, height: 26, px: 0.75, borderRadius: 999,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: '#108ae6', color: '#fff', fontWeight: 800, fontSize: 12.5,
            fontVariantNumeric: 'tabular-nums',
            boxShadow: '0 1px 3px rgba(16,138,230,0.4)',
          }}
        >
          {count}
        </Box>
        <Typography sx={{ fontWeight: 700, color: '#0d6ab0', fontSize: 13.5 }}>
          selected
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>

      <IconButton
        size="small"
        onClick={onClear}
        aria-label="Clear selection"
        sx={{
          color: '#5c6773',
          '&:hover': { bgcolor: 'rgba(16,138,230,0.10)', color: '#108ae6' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}
